import * as React from "react";
import { useEffect, useRef } from "react";
import classNames from "classnames";
import { truncate } from "lodash";
import { AsIconButton } from "app/subframe";

export interface TextAreaEditProps {
    isEditing?: boolean;
    name: string;
    defaultValue?: string;
    onSave?: (value: any) => void;
    onCancel?: () => void;
    valueClassName?: string;
    label?: string;
    valueShownWhenNotEditing?: any;
    isDisabled?: boolean;
    placeholder?: string;
    truncateLength?: number;
    editingClassName?: string;
    containerClassName?: string;
    rows?: number;
}

export const TextAreaEdit: React.FC<TextAreaEditProps> = ({
                                                              name,
                                                              defaultValue,
                                                              isEditing = false,
                                                              onSave,
                                                              valueClassName = "",
                                                              label,
                                                              valueShownWhenNotEditing,
                                                              placeholder,
                                                              isDisabled = false,
                                                              truncateLength = 200,
                                                              editingClassName,
                                                              containerClassName,
                                                              rows = 4,
                                                          }) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [editing, setEditing] = React.useState(isEditing);
    const [value, setValue] = React.useState(defaultValue || "");
    const [displayValue, setDisplayValue] = React.useState(
        valueShownWhenNotEditing || defaultValue
    );

    const handleSave = (e?: React.FormEvent) => {
        e?.preventDefault();
        if (onSave) {
            onSave({ [name]: value });
        }
        setDisplayValue(value);
        setEditing(false);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (
            containerRef.current &&
            !containerRef.current.contains(event.target as Node)
        ) {
            if (onSave) {
                onSave({ [name]: textareaRef.current?.value });
            }
            setValue(textareaRef.current?.value || "");
            setDisplayValue(textareaRef.current?.value || "");
            setEditing(false);
        }
    };

    useEffect(() => {
        if (editing) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [editing]);

    useEffect(() => {
        setValue(defaultValue || "");
        setDisplayValue(defaultValue || "");
    }, [defaultValue]);

    useEffect(() => {
        if (editing && textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(
                textareaRef.current.value.length,
                textareaRef.current.value.length
            );
        }
    }, [editing]);

    return editing ? (
        <div ref={containerRef} className="flex flex-col gap-2 w-full">
            {label && <p className="text-zinc-500 text-sm">{label}</p>}
            <form
                onSubmit={handleSave}
                className="flex items-start w-full gap-2"
            >
        <textarea
            ref={textareaRef}
            name={name}
            rows={rows}
            placeholder={placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className={classNames(
                "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none",
                valueClassName,
                editingClassName
            )}
        />
                {!isDisabled && (
                    <AsIconButton
                        variant="ghost"
                        size="xs"
                        type="submit"
                        leadingIcon="FeatherCheck"
                    />
                )}
            </form>
        </div>
    ) : (
        <div className={classNames("flex flex-col", containerClassName)}>
            {label && <p className="text-zinc-500 text-sm">{label}</p>}
            <div className="flex items-start gap-2">
                <p className={classNames("w-full py-2 whitespace-pre-line", valueClassName)}>
                    {truncate(displayValue || "", {
                        length: truncateLength,
                        omission: "...",
                    })}
                </p>
                {!isDisabled && (
                    <AsIconButton
                        variant="ghost"
                        size="xs"
                        onClick={() => setEditing(true)}
                        leadingIcon="FeatherPen"
                    />
                )}
            </div>
        </div>
    );
};
